import { type FeedbackRequest, newBlankFeedbackRequest } from '@ctw/shared/api/ai/types';
import type { FeedbackSubmissionPolarity } from '@ctw/shared/api/feedback/feedback-manager';
import { Button } from '@ctw/shared/components/button';
import { ErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { FeedbackComponent } from '@ctw/shared/content/chart-review/feedback/feedback.v2';
import type { usePatientSummaries } from '@ctw/shared/content/summarization/use-patient-summaries';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useDrawer } from '@ctw/shared/context/drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { getStandaloneBaseUrl } from '@ctw/shared/utils/environments';
import { tw } from '@ctw/shared/utils/tailwind';
import { pick } from 'lodash-es';
import { useCallback, useState } from 'react';

interface OpenPatientSummaryFeedbackDrawerArgs {
  initialFeedbackPolarity: FeedbackSubmissionPolarity;
}

interface UsePatientFeedbackDrawerProps {
  summaryResponse: ReturnType<typeof usePatientSummaries>;
}

export function usePatientSummaryFeedbackDrawer({
  summaryResponse,
}: UsePatientFeedbackDrawerProps) {
  const telemetry = useTelemetry();
  const { openDrawer, closeDrawer, isDrawerOpen } = useDrawer({
    boundaryName: 'PatientSummaryDrawer',
  });

  return {
    openPatientSummaryFeedbackDrawer: ({
      initialFeedbackPolarity,
    }: OpenPatientSummaryFeedbackDrawerArgs) => {
      openDrawer({
        telemetry,
        action: {
          title: 'Feedback',
          content: {
            body: (
              <PatientSummaryFeedbackDrawerContent
                summaryResponse={summaryResponse}
                initialFeedbackPolarity={initialFeedbackPolarity}
                closeDrawer={closeDrawer}
              />
            ),
          },
        },
      });
    },
    closePatientSummaryFeedbackDrawer: closeDrawer,
    isPatientSummaryFeedbackDrawerOpen: isDrawerOpen,
  };
}

interface PatientSummaryFeedbackDrawerContentProps {
  summaryResponse: UsePatientFeedbackDrawerProps['summaryResponse'];
  initialFeedbackPolarity: OpenPatientSummaryFeedbackDrawerArgs['initialFeedbackPolarity'];
  closeDrawer: ReturnType<typeof useDrawer>['closeDrawer'];
}

const PatientSummaryFeedbackDrawerContent = ({
  summaryResponse,
  initialFeedbackPolarity,
  closeDrawer,
}: PatientSummaryFeedbackDrawerContentProps) => {
  const { requestContext, ctwFetch } = useCTW();
  const telemetry = useTelemetry();
  const [feedbackRequest, setFeedbackRequest] = useState<FeedbackRequest>({
    ...newBlankFeedbackRequest({}),
    isThumbsUp: initialFeedbackPolarity === 'positive',
  });

  const submitFeedback = useCallback(() => {
    void ctwFetch(`${getStandaloneBaseUrl(requestContext.env)}/api/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${requestContext.authToken}`,
      },
      body: JSON.stringify({
        data: {
          type: 'feedback-submission',
          attributes: {
            resourceType: 'PatientSummary',
            resourceId: summaryResponse.summaryJobId,
            polarity: feedbackRequest?.isThumbsUp === true ? 'positive' : 'negative',
            reasons: feedbackRequest?.reasons ?? [],
            notes: feedbackRequest?.content ?? '',
            optedInToOutreach: feedbackRequest?.contactPermission === true,
          },
        },
      }),
    });
  }, [requestContext, ctwFetch, summaryResponse, feedbackRequest]);

  const canSubmitFeedback =
    feedbackRequest !== null &&
    feedbackRequest.isThumbsUp !== undefined &&
    summaryResponse.summaryJobId !== undefined;

  return (
    <ErrorBoundary
      boundaryName="Patient summary feedback drawer content"
      initialContext={{
        ...telemetry.getScopedContextProperties(),
        summaryResponseContext: pick(summaryResponse, [
          'summaryJobId',
          'lastRefreshed',
          'status',
          'error',
        ]),
      }}
    >
      {() => (
        <div className={tw`space-y-3 px-4 py-2`}>
          <FeedbackComponent feedback={feedbackRequest} onFeedbackUpdate={setFeedbackRequest} />
          <div className={tw`flex w-full items-center justify-between gap-5 px-4`}>
            <Button
              type="button"
              variant="link"
              className={tw`hover:underline`}
              onClick={closeDrawer}
            >
              Close
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                if (canSubmitFeedback) {
                  submitFeedback();
                }
              }}
              disabled={!canSubmitFeedback}
            >
              Submit Feedback
            </Button>
          </div>
        </div>
      )}
    </ErrorBoundary>
  );
};
